import { ChevronDownIcon } from "@heroicons/react/24/outline";

import { cn } from "@fronterahealth/frontera-ui-components/lib/utils";
import { AvatarGroup } from "@fronterahealth/frontera-ui-components/ui/avatar";
import { ItemGroup } from "@fronterahealth/frontera-ui-components/ui/item";
import {
  Popover,
  PopoverContent,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
} from "@fronterahealth/frontera-ui-components/ui/popover";

import { ReportReviewerStatus } from "@api/graphql/types-and-hooks";
import { getReviewUserDisplay } from "@components/QaReview/components/commentAuthor";
import { AddReviewerButton } from "@components/ReportReview/AddReviewerButton";
import { ReviewerAvatar } from "@components/ReportReview/ReviewerAvatar";
import { ReviewerItem } from "@components/ReportReview/ReviewerItem";
import { useMyAuth0Id } from "@components/ReportReview/hooks/useMyAuth0Id";
import { useReportReviewers } from "@components/ReportReview/hooks/useReportReviewers";
import { formatReviewTime, latestRequest } from "@components/ReportReview/reviewers";

export const ReviewersPopover: React.FC = () => {
  const { reviewers } = useReportReviewers();
  const myId = useMyAuth0Id();
  const request = latestRequest(reviewers);
  const requestedBy = request?.actor && (request.actor.id === myId ? "you" : getReviewUserDisplay(request.actor).name);
  // useReportReviewers already leaves out withdrawn reviewers.
  const approved = reviewers.filter(({ status }) => status === ReportReviewerStatus.Approved).length;
  const total = reviewers.length;
  const isAllApproved = total > 0 && approved === total;

  return (
    <Popover>
      <PopoverTrigger
        aria-label={`Reviewers, ${approved} of ${total} approved`}
        render={
          <button
            type="button"
            className={cn(
              "flex items-center gap-2 rounded-full border border-limestone-200 py-1 pr-2 pl-3 text-sm text-text-secondary hover:bg-limestone-50",
              isAllApproved && "border-green-100 bg-green-50 text-text-success-primary hover:bg-green-100",
            )}
          />
        }
      >
        <AvatarGroup>
          {reviewers.map((reviewer) => (
            <ReviewerAvatar key={reviewer.id} reviewer={reviewer} />
          ))}
        </AvatarGroup>
        {total > 0 && (
          <span className="tabular-nums">
            {approved} of {total} approved
          </span>
        )}
        <ChevronDownIcon className="size-4 text-limestone-600" />
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96">
        <PopoverHeader className="flex-row items-center justify-between">
          <PopoverTitle>Reviewers</PopoverTitle>
          <AddReviewerButton />
        </PopoverHeader>
        <ItemGroup className="gap-2">
          {reviewers.map((reviewer) => (
            <ReviewerItem key={reviewer.id} reviewer={reviewer} />
          ))}
        </ItemGroup>
        {request && requestedBy && (
          <p className="border-t border-limestone-200 pt-2 text-xs text-text-tertiary">
            Requested by {requestedBy} {formatReviewTime(request.createdDate)}
          </p>
        )}
      </PopoverContent>
    </Popover>
  );
};
